import { Link } from "react-router-dom";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import { useDataQuery } from "@/hooks/useDataQuery";
import { getDataClient } from "@/lib/dataClient";

interface DashboardStats {
  ogrenci: number;
  ogretmen: number;
  veli: number;
  ders: number;
  soru: number;
  canliDers: number;
  odev: number;
  sinav: number;
}

interface RecentStudent {
  id: string;
  full_name: string | null;
  grade: string | null;
  city: string | null;
  created_at: string;
}

const quickActions = [
  { label: "Kullanıcılar", href: "/yonetici/kullanicilar", icon: "ri-group-line" },
  { label: "Roller ve Yetkiler", href: "/yonetici/roller", icon: "ri-shield-keyhole-line" },
  { label: "İçerik Yönetimi", href: "/yonetici/icerikler", icon: "ri-stack-line" },
  { label: "Yapay Zekâ Yönetimi", href: "/yonetici/ai-yonetimi", icon: "ri-brain-line" },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toLocaleUpperCase("tr-TR");
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase("tr-TR");
}

async function countRows(dataClient: ReturnType<typeof getDataClient>, table: string): Promise<number> {
  const { count, error } = await dataClient
    .from(table)
    .select("*", { count: "exact", head: true });
  if (error) return 0;
  return count ?? 0;
}

export default function AdminDashboard() {
  const { data, loading, error, reload } = useDataQuery<{
    stats: DashboardStats;
    recent: RecentStudent[];
  }>(async () => {
    const dataClient = getDataClient();

    const [ogrenci, ogretmen, veli, ders, soru, canliDers, odev, sinav] = await Promise.all([
      dataClient.from("profiles").select("*", { count: "exact", head: true }).eq("role", "ogrenci"),
      dataClient.from("profiles").select("*", { count: "exact", head: true }).eq("role", "ogretmen"),
      dataClient.from("profiles").select("*", { count: "exact", head: true }).eq("role", "veli"),
      countRows(dataClient, "courses"),
      countRows(dataClient, "question_bank"),
      countRows(dataClient, "live_classes"),
      countRows(dataClient, "homework"),
      countRows(dataClient, "exams"),
    ]);

    const recentRes = await dataClient
      .from("profiles")
      .select("id, full_name, grade, city, created_at")
      .eq("role", "ogrenci")
      .order("created_at", { ascending: false })
      .limit(5);

    if (recentRes.error) throw new Error(recentRes.error.message);

    return {
      stats: {
        ogrenci: ogrenci.count ?? 0,
        ogretmen: ogretmen.count ?? 0,
        veli: veli.count ?? 0,
        ders,
        soru,
        canliDers,
        odev,
        sinav,
      },
      recent: (recentRes.data ?? []) as RecentStudent[],
    };
  }, []);

  const statCards = data
    ? [
        { label: "Öğrenci", value: data.stats.ogrenci, icon: "ri-graduation-cap-line", color: "bg-primary-100 text-primary-700" },
        { label: "Öğretmen", value: data.stats.ogretmen, icon: "ri-user-star-line", color: "bg-secondary-100 text-secondary-900" },
        { label: "Veli", value: data.stats.veli, icon: "ri-heart-3-line", color: "bg-accent-100 text-accent-800" },
        { label: "Ders", value: data.stats.ders, icon: "ri-book-2-line", color: "bg-primary-100 text-primary-700" },
        { label: "Soru", value: data.stats.soru, icon: "ri-list-check-2", color: "bg-accent-100 text-accent-800" },
        { label: "Canlı Ders", value: data.stats.canliDers, icon: "ri-vidicon-line", color: "bg-secondary-100 text-secondary-900" },
        { label: "Ödev", value: data.stats.odev, icon: "ri-task-line", color: "bg-primary-100 text-primary-700" },
        { label: "Sınav", value: data.stats.sinav, icon: "ri-file-list-3-line", color: "bg-accent-100 text-accent-800" },
      ]
    : [];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Ana Panel"
        description="Platformdaki öğrenciler, içerikler ve etkinliklere genel bakış."
      />

      {error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 flex flex-col items-center justify-center text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-xl" aria-hidden="true"></i>
          </span>
          <p className="mt-3 font-heading font-semibold text-sm text-foreground-700">
            Veriler yüklenemedi
          </p>
          <p className="mt-1 text-sm text-foreground-500 max-w-md">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            Tekrar dene
          </button>
        </div>
      ) : (
        <>
          {/* İstatistik kartları */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-background-200 bg-background-50 p-5 animate-pulse"
                  >
                    <div className="w-10 h-10 rounded-md bg-background-100"></div>
                    <div className="mt-3 h-6 w-12 bg-background-100 rounded"></div>
                    <div className="mt-2 h-3 w-16 bg-background-100 rounded"></div>
                  </div>
                ))
              : statCards.map((c) => (
                  <div
                    key={c.label}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span
                      className={`w-10 h-10 flex items-center justify-center rounded-md ${c.color} shrink-0`}
                    >
                      <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <p className="mt-3 text-2xl font-heading font-bold text-foreground-950">
                      {c.value}
                    </p>
                    <p className="mt-0.5 text-sm text-foreground-500">{c.label}</p>
                  </div>
                ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Son kayıt olan öğrenciler */}
            <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-sm text-foreground-950">
                  Son Kayıt Olan Öğrenciler
                </h2>
                <Link
                  to="/yonetici/kullanicilar"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
                >
                  Tümü
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </Link>
              </div>
              <div className="mt-4 space-y-2">
                {loading ? (
                  <p className="text-sm text-foreground-500">Yükleniyor...</p>
                ) : (data?.recent ?? []).length === 0 ? (
                  <p className="text-sm text-foreground-500">Henüz öğrenci kaydı yok.</p>
                ) : (
                  (data?.recent ?? []).map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-background-100/60 transition-colors"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 text-xs font-semibold shrink-0">
                        {initialsOf(s.full_name ?? "?")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {s.full_name ?? "—"}
                        </p>
                        <p className="text-xs text-foreground-500 truncate">
                          {s.grade ?? ""}
                          {s.grade && s.city ? " · " : ""}
                          {s.city ?? ""}
                        </p>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {new Date(s.created_at).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Hızlı işlemler */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-sm text-foreground-950">
                Hızlı İşlemler
              </h2>
              <div className="mt-4 space-y-2">
                {quickActions.map((a) => (
                  <Link
                    key={a.href}
                    to={a.href}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-background-100/60 transition-colors"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                      <i className={`${a.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                      {a.label}
                    </span>
                    <i className="ri-arrow-right-s-line ml-auto text-foreground-400" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}